import { TFunction } from "i18next";
import { Suspense, useState } from "react";
import { useWatch } from "react-hook-form";
import { useTranslation } from "react-i18next";

import MaterialsVolumetricPriceTable from "#modules/company/view/MaterialsVolumetricPriceTable";
import { OverThicknessFormulas } from "#modules/company/view/OverThicknessFormulas";
import { SectionProps } from "#modules/company/view/QuoteSettingsFormSections";
import { formatErrorWithLocation } from "#modules/materials/helpers/materialErrorFormatter";
import { Icon } from "#shared/icons/Icon";
import { Alert } from "#shared/view/components/Alert";
import { CheckboxController } from "#shared/view/components/CheckboxController";
import { Flex } from "#shared/view/components/Flex";
import { Space } from "#shared/view/components/Space";
import { Stack } from "#shared/view/components/Stack";
import { Title } from "#shared/view/components/Title";
import { NativeSelectInputController } from "#shared/view/designSystem/inputs/NativeSelectInputController";
import { Skeleton } from "#shared/view/designSystem/Skeleton";
import {
  MaterialVolumetricPricingStrategySchema,
  PricingModeSchema,
  MaterialUploadError,
  NestingModeSchema,
} from "shared";

import { MaterialsPriceTable } from "./MaterialsPriceTable";

const MATERIAL_PRICING_MODE_OPTIONS = (t: TFunction) => [
  {
    label: t("settings.materials.pricingModeRaw"),
    value: PricingModeSchema.enum.RAW,
  },
  {
    label: t("settings.materials.pricingModeVolumetric"),
    value: PricingModeSchema.enum.VOLUMETRIC,
  },
];

const MATERIAL_NESTING_MODE_OPTIONS = (t: TFunction) => [
  {
    label: t("settings.materials.nestingMode.options.automatic"),
    value: NestingModeSchema.enum.AUTOMATIC,
  },
  {
    label: t("settings.materials.nestingMode.options.always"),
    value: NestingModeSchema.enum.ALWAYS,
  },
  {
    label: t("settings.materials.nestingMode.options.never"),
    value: NestingModeSchema.enum.NEVER,
  },
];

const MATERIAL_VOLUMETRIC_PRICING_STRATEGY_OPTIONS = (t: TFunction) => [
  {
    label: t("settings.materials.volumetricPricingStrategyRealWeight"),
    value: MaterialVolumetricPricingStrategySchema.enum.REAL_WEIGHT,
  },
  {
    label: t("settings.materials.volumetricPricingStrategyCapableRectangle"),
    value: MaterialVolumetricPricingStrategySchema.enum.CAPABLE_RECTANGLE,
  },
];

export const MaterialsSettings = ({ control }: SectionProps) => {
  const { t } = useTranslation();
  const [uploadErrors, setUploadErrors] = useState<MaterialUploadError[]>([]);

  const materialPricingMode = useWatch({ control, name: "materialPricingMode" });
  const isVolumetricPricing = materialPricingMode === PricingModeSchema.enum.VOLUMETRIC;

  return (
    <Stack gap={0}>
      <Title order={5}>Matière</Title>

      {uploadErrors.length > 0 && (
        <>
          <Space h="sm" />
          <Alert
            color="red"
            icon={<Icon name="alertTriangle" />}
            title={t("settings.materials.upload.validationErrors")}
            withCloseButton
            onClose={() => setUploadErrors([])}
          >
            {uploadErrors.slice(0, 10).map((error, index) => (
              <div key={index}>{formatErrorWithLocation(error, t)}</div>
            ))}
          </Alert>
        </>
      )}

      <Space h="sm" />
      <Flex gap="xs">
        <NativeSelectInputController
          name="materialPricingMode"
          data={MATERIAL_PRICING_MODE_OPTIONS(t)}
          label={t("settings.materials.pricingMode")}
          control={control}
          withEmptyData={false}
        />
        {isVolumetricPricing && (
          <NativeSelectInputController
            name="materialVolumetricPricingStrategy"
            data={MATERIAL_VOLUMETRIC_PRICING_STRATEGY_OPTIONS(t)}
            label={t("settings.materials.materialVolumetricPricingStrategy")}
            control={control}
            withEmptyData={false}
          />
        )}
      </Flex>

      <Space h="sm" />

      {materialPricingMode === PricingModeSchema.enum.RAW ? (
        <MaterialsPriceTable setUploadErrors={setUploadErrors} uploadErrors={uploadErrors} />
      ) : (
        <Suspense fallback={<Skeleton height={200} width="100%" />}>
          <MaterialsVolumetricPriceTable />
        </Suspense>
      )}

      <Space h="sm" />
      <Flex gap="xs" align="flex-end">
        <NativeSelectInputController
          name="nestingMode"
          data={MATERIAL_NESTING_MODE_OPTIONS(t)}
          label={t("settings.materials.nestingMode.label")}
          control={control}
          withEmptyData={false}
          disabled={isVolumetricPricing}
        />
        <CheckboxController
          name="hasMultiPartNesting"
          label={t("settings.materials.multiPartNesting")}
          control={control}
          disabled={isVolumetricPricing}
        />
      </Flex>
      <Space h="sm" />

      <OverThicknessFormulas />

      <Space h="lg" />
    </Stack>
  );
};
